import React, { useState, useCallback, useEffect, useMemo } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { useDispatch, useSelector } from 'react-redux';
import { setField } from 'actions/nodeSettings';
import {
  activationFunctions,
  getDefaultMembershipFunctions,
} from 'constants/nodes';
import {
  SettingsNumberField,
  SettingsDropdown,
  SettingsControlLabel,
} from 'components/SettingsNew';
import NodeSettingsMembershipFunctionShapeGraph from 'components/nodes/NodeSettingsMembershipFunctionShapeGraph';
import text from 'constants/text';
import { populationUnits } from 'constants/nodes';
import {
  augmentedMembership,
  membershipCategoryFilter,
  rescale,
  descale,
} from 'fcm/fcm';
import './NodeSettings.scss';

const makeOption = value => ({ label: value, value });

// Advanced settings
const MatrixAdvancedSettings = ({ matrix, readOnly, node, isEditing }) => {
  const dispatch = useDispatch();
  const { validationMessages } = useSelector(state => state.matrixSettings);

  // TODO check this, no longer valid
  const modalRef = document.getElementById('matrix-modal');

  const [graphWidth, setGraphWidth] = useState(400);

  const getGraphWidth = useCallback(e => {
    const { contentRect } = e;
    const { width } = contentRect;
    setGraphWidth(width);
  }, []);

  useResizeObserver(
    document?.getElementById('NodeSettingsMembershipFunctionShapeGraph'),
    getGraphWidth
  );

  useEffect(() => {
    const el = document?.getElementById(
      'NodeSettingsMembershipFunctionShapeGraph'
    );
    setGraphWidth(el.offsetWidth);
  }, [getGraphWidth]);

  const membershipFunctionOptions = [
    { label: '3 Fuzzy Categories', value: 3 },
    { label: '5 Fuzzy Categories', value: 5 },
  ];

  const onChangeFuzzyCategories = useCallback(
    v => {
      const nextFuzzyCategories = getDefaultMembershipFunctions(v);
      dispatch(setField('membershipFunctions', nextFuzzyCategories));
    },
    [dispatch]
  );

  // Abundance
  const membershipCategories = membershipCategoryFilter(
    augmentedMembership,
    node?.membershipFunctions
  );

  const [minValue, setMin] = useState(
    node?.membershipFunctions[membershipCategories[0]][0][0]
  );
  const [meanValue, setMean] = useState(node?.mean);
  const [maxValue, setMax] = useState(
    node?.membershipFunctions[
      membershipCategories[membershipCategories.length - 1]
    ][2][0]
  );

  const conditionalUpdate = () => {
    if (isNaN(minValue) || isNaN(meanValue) || isNaN(maxValue)) return;
    if (minValue >= meanValue || minValue >= maxValue) return;
    if (maxValue <= meanValue || maxValue <= minValue) return;

    let scaledFunctions = rescale(
      JSON.parse(JSON.stringify(node?.membershipFunctions)),
      node?.mean
    );
    let newFunctions = descale(scaledFunctions, meanValue, minValue, maxValue);

    dispatch(setField('membershipFunctions', newFunctions));
    dispatch(setField('mean', meanValue));
  };

  useEffect(conditionalUpdate, [minValue, meanValue, maxValue]);

  return (
    <div className="NodeSettingsAdvanced">
      <div className="NodeSettings-row">
        <div className="NodeSettings-column NodeSettings-half">
          {/* Abundance units */}
          {/* TODO update to use value and label rather than just label */}
          <SettingsDropdown
            disabled={readOnly}
            label="Abundance units"
            onChange={inputValue => {
              dispatch(
                setField(
                  'populationUnit',
                  populationUnits.find(obj => {
                    return obj.displayName === inputValue;
                  }).name
                )
              );
            }}
            options={populationUnits
              .map(({ displayName }) => displayName)
              .map(makeOption)}
            value={
              populationUnits.find(obj => {
                return obj.name === node.populationUnit;
              })?.displayName || node.populationUnit
            }
            menuPortalTarget={document.body}
            closeMenuOnScroll={event =>
              modalRef && modalRef.firstChild.contains(event.target)
            }
            description="Specify how the population is being calculate in the matrix"
            isStatic={!isEditing}
          />
          {/* Membership graph */}
          <div
            id="NodeSettingsMembershipFunctionShapeGraph"
            className="NodeSettings-column"
          >
            <SettingsControlLabel label="Fuzzy functions" />
            <NodeSettingsMembershipFunctionShapeGraph
              membershipFunctions={node.membershipFunctions}
              mean={node.mean}
              readOnly={readOnly}
              width={graphWidth}
              height={175}
              isStatic={!isEditing}
            />
          </div>
          {/* Min/mode/max */}
          <div className="NodeSettings-number-input-row">
            <SettingsNumberField
              disabled={readOnly}
              key={0}
              label="Min"
              onBlur={v => setMin(+v)}
              onChange={v => setMin(+v)}
              value={minValue}
              validationMessage={
                +minValue >= +meanValue ||
                +minValue >= +maxValue ||
                isNaN(minValue)
                  ? 'Invalid min'
                  : ''
              }
              isStatic={!isEditing}
            />
            <SettingsNumberField
              disabled={readOnly}
              key={1}
              label="Mode"
              onBlur={v => setMean(+v)}
              onChange={v => setMean(+v)}
              validationMessage={
                +minValue >= +meanValue ||
                +meanValue >= +maxValue ||
                isNaN(meanValue)
                  ? 'Invalid mode'
                  : ''
              }
              value={meanValue}
              isStatic={!isEditing}
            />
            <SettingsNumberField
              disabled={readOnly}
              key={2}
              label="Max"
              onBlur={v => setMax(+v)}
              onChange={v => setMax(+v)}
              validationMessage={
                +minValue >= +maxValue ||
                +meanValue >= +maxValue ||
                isNaN(meanValue)
                  ? 'Invalid max'
                  : ''
              }
              value={maxValue}
              isStatic={!isEditing}
            />
          </div>
        </div>

        <div className="NodeSettings-column NodeSettings-half">
          <SettingsNumberField
            label="Abundance exaggeration"
            disabled={readOnly}
            onChange={value =>
              dispatch(setField('abundanceExaggeration', +value))
            }
            validationMessage={validationMessages.abundanceExaggeration}
            tooltip={text.nodeSettingsToolTip['abundanceExaggeration']}
            value={node.abundanceExaggeration}
            description="Set the multiplicative factor to increase this node's abundance in the system. This should only be used for basal nodes in cases where the original basal node abundance is not large enough to ensure that the stable state of other nodes within the system is above 0."
            isStatic={!isEditing}
          />
          {/* TODO update to use value and label rather than just label */}
          <SettingsDropdown
            label="Activation function"
            disabled={readOnly}
            options={activationFunctions
              .map(af => af.displayName)
              .map(makeOption)}
            onChange={value =>
              dispatch(
                setField(
                  'activationFunction',
                  activationFunctions.find(obj => {
                    return obj.displayName === value;
                  }).name
                )
              )
            }
            placeholder="Select Activation Function"
            value={
              activationFunctions.find(obj => {
                return obj.name === node.activationFunction;
              }).displayName
            }
            menuPortalTarget={document.body}
            closeMenuOnScroll={event =>
              modalRef && modalRef.firstChild.contains(event.target)
            }
            description="The standard activation function is exponential, so if you aren't sure, use that. Logistic generally applies to basal resources such as precipitation or sun."
            isStatic={!isEditing}
          />
          {/* TODO update to use value and label rather than just label */}
          <SettingsDropdown
            label="Fuzzy categories"
            disabled={
              readOnly || !Object.keys(node?.membershipFunctions)?.length
            }
            options={membershipFunctionOptions}
            onChange={onChangeFuzzyCategories}
            placeholder="Select number of fuzzy categories"
            value={Object.keys(node?.membershipFunctions)?.length ?? 0}
            menuPortalTarget={document.body}
            closeMenuOnScroll={event =>
              modalRef && modalRef.firstChild.contains(event.target)
            }
            description="Changing the number of fuzzy categories will reset your graph to a default"
            isStatic={!isEditing}
          />
        </div>
      </div>
    </div>
  );
};

export default MatrixAdvancedSettings;
